import { defineStore } from 'pinia'
import { useFetch, useGeolocation } from '@vueuse/core'
import { computed, watch, ref } from 'vue'
import { useIntelichekFetch } from '@/lib/intelichek-fetch.js'
import { useI18n } from 'vue-i18n'
import { GeocodingError, ValidationError } from '@/lib/errors.js'

export const useGeocodeStore = defineStore('geocode', () => {
  // I18n
  const { t } = useI18n()

  // Cached location state
  const cachedLocation = ref(null)
  const locationParsingError = ref(null)

  const { coords, error: geolocationError } = useGeolocation({
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 300000
  })
  const latLng = computed(() => {
    if (!coords.value || coords.value.latitude === Infinity || coords.value.longitude === Infinity) return null

    return [coords.value.latitude, coords.value.longitude]
  })

  const geocodeUrl = computed(() => {
    if (!latLng.value) return null
    return `https://nominatim.openstreetmap.org/reverse?lat=${latLng.value[0]}&lon=${latLng.value[1]}&format=json`
  })
  const { data: geocodeData, isFetching: geocodeIsFetching, error: geocodingError } = useFetch(geocodeUrl, { 
    refetch: true,
    beforeFetch({ url, options, cancel }) {
      if (!url) cancel()
      options.headers = {
        ...options.headers,
        'Referer': window.location.origin
      }
      return { url, options }
    },
    afterFetch({ data, response }) {
      if (!response.ok) {
        throw new Error(`Geocoding API error: ${response.status}`)
      }
      return { data, response }
    }
  }).json()

  const stateUrl = computed(() => {
    if (!geocodeData.value) return null

    const url = new URL(`${import.meta.env.VITE_BACKEND_URL}/api/v2/us_states`)
    url.searchParams.append('query[name_eq]', geocodeData.value.address.state)
    return url.href
  })
  // const stateUrl = computed(() => `${import.meta.env.VITE_BACKEND_URL}/api/v2/us_states?query[name_eq]=${((geocodeData.value || {}).address || {}).state}`)
  const { data: stateData, isFetching: stateIsFetching } = useIntelichekFetch(stateUrl, { refetch: true }).json()

  const userLocation = computed(() => {
    // Check cached location first
    if (cachedLocation.value) {
      // Check if location has expired (30 days)
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000)
      if (!cachedLocation.value.timestamp || cachedLocation.value.timestamp < thirtyDaysAgo) {
        cachedLocation.value = null
      } else if (coords.value && Math.abs(cachedLocation.value.latitude - coords.value.latitude) > 0.1) {
        // Location has changed significantly, clear cache
        cachedLocation.value = null
      } else {
        return cachedLocation.value
      }
    }

    // Generate location from geolocation
    if (coords.value && geocodeData.value && stateData.value?.data?.[0]) {
      let state_display = geocodeData.value.address.state
      try {
        state_display = stateData.value.data[0].attributes.abbreviation
        const city_display = geocodeData.value.address.municipality || geocodeData.value.address.city || geocodeData.value.address.town || geocodeData.value.address.village || geocodeData.value.address.city_district

        const location = {
          id: 0,
          nickname: `${city_display}, ${state_display}`,
          location_name: t('request_a_quote.location_section.location_dropdown.your_location'),
          address: `${geocodeData.value.address.house_number || ""} ${geocodeData.value.address.road}`.trim(),
          address_2: null,
          city: city_display,
          state: state_display,
          country: geocodeData.value.address.country,
          latitude: coords.value.latitude,
          longitude: coords.value.longitude,
          timestamp: Date.now()
        }
        
        // Cache the location
        cachedLocation.value = location
        return location
      } catch (e) {
        locationParsingError.value = { message: "Failed to Parse Geocoding", error: e }
        return {}
      }
    } else {
      return {}
    }
  })

  const isLoading = computed(() => {
    return (!coords.value || geocodeIsFetching || stateIsFetching) && !isError
  })

  const isError = computed(() => {
    return !!geolocationError.value || !!locationParsingError.value
  })

  async function fetchAddressLocation(address, omitCity = false) {
    if (!address.address || !address.city || !address.state_abr || !address.zip) {
      throw new ValidationError("Incomplete form.")
    }

    const url = new URL('https://nominatim.openstreetmap.org/search')
    url.searchParams.append('format', 'json')
    url.searchParams.append('limit', 1)
    url.searchParams.append('street', address.address)
    if (!omitCity) {
      url.searchParams.append('city', address.city)
    }
    url.searchParams.append('state', address.state_abr)
    if (address.country) {
      url.searchParams.append('country', address.country)
    }
    url.searchParams.append('postalcode', address.zip)

    const result = await fetch(url.href, {
      headers: {
        'Referer': window.location.origin
      }
    })

    // Try again omitting city
    if (!result.ok && omitCity === false) {
      return await fetchAddressLocation(address, true)
    } else if (!result.ok) {
      throw new GeocodingError(`Failed to fetch address location: ${result.status} ${result.statusText}`)
    }

    let results
    try {
      const text = await result.text()
      if (!text.trim().startsWith('[') && !text.trim().startsWith('{')) {
        throw new Error('Invalid JSON response')
      }
      results = JSON.parse(text)
    } catch (e) {
      throw new GeocodingError('Invalid response from geocoding service')
    }

    if (results.length === 0 && omitCity === false) {
      return await fetchAddressLocation(address, true)
    } else if (results.length === 0) {
      throw new GeocodingError("Failed to fetch address location.")
    }

    return results
  }

  function $reset() {
    cachedLocation.value = null
    // Force fresh geolocation request with better error handling
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          // Show success toast
          try {
            const { useToastStore } = await import('@/stores/toast.js');
            const toastStore = useToastStore();
            toastStore.success(t('toast.location_refreshed'));
          } catch (e) {
            // Toast notification failed silently
          }
        },
        (error) => {},
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      )
    }
  }



  return {
    geocodeUrl,
    isError,
    geolocationError,
    geocodingError,
    locationParsingError,
    isLoading,
    latLng,
    userLocation,
    fetchAddressLocation,
    $reset,
  }
}, {
  persist: {
    key: 'geocode-store',
    paths: ['cachedLocation']
  }
})
